import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';

interface ReportResult {
  data: {
    period: string;
    totalParticipants: number;
    totalSessions: number;
    avgPhq9: number;
    avgGad7: number;
    severityDistribution: { mild: number; moderate: number; severe: number };
    topConcerns: string[];
    sessionsPerPerson: number;
    engagementRate: number;
  };
  narrative: string;
}

export function ReportsPage() {
  const [reportType, setReportType] = useState<'corporate' | 'campus'>('corporate');
  const [entityId, setEntityId] = useState('');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingReports, setPendingReports] = useState<
    { id: string; targetType: string; targetId: string; month: string; narrative: string }[]
  >([]);

  useEffect(() => {
    adminApi<{ data: typeof pendingReports }>('/admin/reports/pending')
      .then((d) => setPendingReports(ensureArray(d.data)))
      .catch(() => setPendingReports([]));
  }, []);

  const approvePendingReport = async (id: string) => {
    await adminApi(`/admin/reports/pending/${id}/approve`, { method: 'POST' });
    setPendingReports((prev) => prev.filter((r) => r.id !== id));
  };

  const generateReport = async () => {
    if (!entityId.trim()) {
      setError('Entity ID is required.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const data = await adminApi<ReportResult>(`/reports/${reportType}/${entityId}/${month}`);
      setResult(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold text-gray-900">Wellness Reports</h1>

      {pendingReports.length > 0 && (
        <div className="card mb-6 border-amber-200 bg-amber-50">
          <h2 className="mb-3 text-lg font-semibold text-amber-900">
            Pending First-Delivery Review
          </h2>
          <div className="space-y-3">
            {pendingReports.map((report) => (
              <div
                key={report.id}
                className="flex items-center justify-between rounded-lg border border-amber-100 bg-white p-3"
              >
                <div>
                  <p className="font-medium">
                    {report.targetType} — {report.month}
                  </p>
                  <p className="line-clamp-2 text-sm text-gray-600">{report.narrative}</p>
                </div>
                <button
                  onClick={() => void approvePendingReport(report.id)}
                  className="btn-primary text-sm"
                >
                  Approve & Send
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card mb-6">
        <h2 className="mb-4 text-lg font-semibold">Generate Report</h2>
        <div className="flex flex-wrap gap-4">
          <select
            className="rounded-lg border p-2 text-sm"
            value={reportType}
            onChange={(e) => setReportType(e.target.value as typeof reportType)}
          >
            <option value="corporate">Corporate</option>
            <option value="campus">Campus</option>
          </select>

          <input
            className="rounded-lg border p-2 text-sm"
            placeholder="Entity ID"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
          />
          <input
            type="month"
            className="rounded-lg border p-2 text-sm"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />

          <button onClick={() => void generateReport()} disabled={loading} className="btn-primary">
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </div>

      {result && (
        <div className="space-y-4">
          <div className="card">
            <h2 className="mb-3 text-lg font-semibold">Metrics — {result.data.period}</h2>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-sm text-gray-500">Participants</p>
                <p className="text-xl font-bold">{result.data.totalParticipants}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Sessions</p>
                <p className="text-xl font-bold">{result.data.totalSessions}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Engagement Rate</p>
                <p className="text-xl font-bold">{result.data.engagementRate}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg PHQ-9</p>
                <p className="text-xl font-bold">{result.data.avgPhq9}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg GAD-7</p>
                <p className="text-xl font-bold">{result.data.avgGad7}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Sessions/Person</p>
                <p className="text-xl font-bold">{result.data.sessionsPerPerson}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="mb-1 text-sm text-gray-500">Severity Distribution</p>
              <div className="flex gap-2">
                <span className="rounded bg-green-100 px-2 py-1 text-xs text-green-800">
                  Mild: {result.data.severityDistribution.mild}
                </span>
                <span className="rounded bg-yellow-100 px-2 py-1 text-xs text-yellow-800">
                  Moderate: {result.data.severityDistribution.moderate}
                </span>
                <span className="rounded bg-red-100 px-2 py-1 text-xs text-red-800">
                  Severe: {result.data.severityDistribution.severe}
                </span>
              </div>
            </div>
            {result.data.topConcerns.length > 0 && (
              <div className="mt-4">
                <p className="mb-1 text-sm text-gray-500">Top Concerns</p>
                <div className="flex flex-wrap gap-1">
                  {result.data.topConcerns.map((c) => (
                    <span
                      key={c}
                      className="bg-primary-50 text-primary-700 rounded-full px-2 py-0.5 text-xs"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <h2 className="mb-3 text-lg font-semibold">AI Narrative</h2>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
              {result.narrative}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
